import { useCallback, useEffect, useRef, useState } from "react";

const isBrowser = typeof window !== "undefined";

const safelyParse = (value, parse, fallback) => {
  if (value === null || value === undefined) {
    return fallback;
  }

  if (!parse) {
    return value;
  }

  try {
    return parse(value);
  } catch (error) {
    console.warn("Failed to parse persisted state value:", error);
    return fallback;
  }
};

/**
 * Persist a piece of state to localStorage with optional validation and parsing.
 * Designed for App-level state that should survive reloads.
 */
export const usePersistentState = (
  key,
  defaultValue,
  {
    parser = JSON.parse,
    serializer = JSON.stringify,
    raw = false,
    validator,
  } = {},
) => {
  const parseFn = raw ? undefined : parser;
  const serializeFn = raw ? (value) => value : serializer;
  const fallbackRef = useRef(defaultValue);

  const readValue = useCallback(() => {
    if (!isBrowser) {
      return fallbackRef.current;
    }

    const storedValue = window.localStorage.getItem(key);
    const parsedValue = safelyParse(storedValue, parseFn, fallbackRef.current);
    return validator ? validator(parsedValue, fallbackRef.current) : parsedValue;
  }, [key, parseFn, validator]);

  const [state, setState] = useState(readValue);

  const persistValue = useCallback(
    (nextValue) => {
      setState((prev) => {
        const resolvedValue =
          typeof nextValue === "function" ? nextValue(prev) : nextValue;
        const validatedValue = validator
          ? validator(resolvedValue, fallbackRef.current)
          : resolvedValue;

        if (Object.is(validatedValue, prev)) {
          return prev;
        }

        if (!isBrowser) {
          return validatedValue;
        }

        if (validatedValue === undefined || validatedValue === null) {
          window.localStorage.removeItem(key);
        } else {
          const valueToStore = serializeFn(validatedValue);
          window.localStorage.setItem(key, valueToStore);
        }

        return validatedValue;
      });
    },
    [key, serializeFn, validator],
  );

  useEffect(() => {
    if (!isBrowser) return;

    const handleStorage = (event) => {
      if (event.key !== key) return;

      setState(
        validator
          ? validator(
              safelyParse(event.newValue, parseFn, fallbackRef.current),
              fallbackRef.current,
            )
          : safelyParse(event.newValue, parseFn, fallbackRef.current),
      );
    };

    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, [key, parseFn, validator]);

  return [state, persistValue];
};

export default usePersistentState;
